import { NgModule, ErrorHandler } from '@angular/core';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing.module';
import { PageNotFoundComponent } from './page-not-found.component';
import { SharedModule } from './modules/shared/shared.module';
import { ThirdPartyModule } from './modules/third-party/third-party.module';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { HttpErrorInterceptor } from './http-error.interceptor';
import { ErrorStateMatcher } from '@angular/material/core';
import { IntlModule } from '@progress/kendo-angular-intl';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { GlobalErrorHandler } from './global-error-handler';
import { JwtInterceptor } from './modules/shared/interceptors/jwt.interceptor';
import { LimegearAuthenticationService } from './modules/login/services/limegear-authentication.service';
import { CurrentUserStateService } from './modules/shared/services/current-user-state.service';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { ReportExportService } from './modules/shared/services/report-export.service';
import { StandaloneComponentsModule } from './modules/my-limegear/components/standalone-components/standalone-components.module';
import { MasterNavigationModule } from './modules/master-navigation/master-navigation.module';
import { AuthModule } from 'angular-auth-oidc-client';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { ImpersonationService } from 'app/modules/shared/services/impersonation.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';
import { definePreset } from '@primeng/themes';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { ToastService } from 'app/modules/shared/services/toast.service';
import { UserManagementService } from 'app/modules/shared/services/user-management.service';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';

const baseUrl =
    'https://' + window.location.hostname.toLowerCase().replace('localhost', 'localhost:44388');

const MaterialBlue = definePreset(Material, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}',
        },
    },
});

@NgModule({
    declarations: [AppComponent, PageNotFoundComponent],
    imports: [
        BrowserAnimationsModule,
        BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
        CommonModule,
        AppRoutingModule,
        SharedModule,
        ThirdPartyModule,
        IntlModule,
        DateInputsModule,
        StandaloneComponentsModule,
        MatIconModule,
        MatMenuModule,
        MatCardModule,
        NgIdleKeepaliveModule.forRoot(),
        MasterNavigationModule,
        AuthModule.forRoot({
            config: [
                {
                    configId: 'newamericanfundingllc',
                    authority: 'https://auth.nafinc.com/as',
                    clientId: '2a8844ec-5374-4cc3-b628-bab969616196',
                    redirectUrl: baseUrl + '/login/callback/openid',
                    postLogoutRedirectUri: baseUrl,
                    responseType: 'id_token',
                    scope: 'openid email',
                    silentRenew: false,
                    useRefreshToken: false,
                },
                {
                    configId: 'dartbank',
                    authority: 'https://dartbank.okta.com',
                    clientId: '0oajs86bqhlDgtzwi5d7',
                    redirectUrl: baseUrl + '/login/callback/openid',
                    postLogoutRedirectUri: baseUrl,
                    responseType: 'id_token',
                    scope: 'openid email',
                    silentRenew: false,
                    useRefreshToken: false,
                },
            ],
        }),
    ],
    providers: [
        // Services added in this section will persist the same instance for all components
        ErrorStateMatcher,
        DecimalPipe,
        DatePipe,
        LimegearAuthenticationService,
        CurrentUserStateService,
        ImpersonationService,
        UserManagementService,
        UserAccessService,
        ToastService,
        HttpClientModule,
        ReportExportService,
        MessageService,
        ConfirmationService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptor,
            multi: true,
        },
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: MaterialBlue,
                options: {
                    darkModeSelector: 'dark',
                    cssLayer: false,
                },
            },
        }),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
